import React, { useState, useEffect } from 'react';
import {
  TextField, FormControl, FormLabel, RadioGroup, FormControlLabel,
  Radio, InputLabel, Select, MenuItem, Typography, Box, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, Divider, useMediaQuery, useTheme, IconButton,
  InputAdornment, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { ScheduleBasicInfoProps } from './types';
import { Schedule } from '../../../types/schedule';
import { OtherSchedule } from '../../../types/otherSchedule';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';


export const ScheduleBasicInfo: React.FC<ScheduleBasicInfoProps> = ({
  scheduleType,
  formData,
  onChange,
  onRadioChange,
  onTimeTypeChange,
  onConstructionSelect,
  onCustomerSelect,
  constructions = [],
  customers = [],
  isLoadingData = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 工事選択ダイアログの状態
  const [constructionDialogOpen, setConstructionDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [expandedCustomers, setExpandedCustomers] = useState<string[]>([]);

  // 集合時間選択ダイアログの状態
  const [meetingTimeDialogOpen, setMeetingTimeDialogOpen] = useState(false);

  // 時間選択用の状態
  const [selectedHour, setSelectedHour] = useState<number>(9);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);

  // 顧客別に工事をグループ化
  const groupedConstructions = React.useMemo(() => {
    const groups: Record<string, typeof constructions> = {};

    constructions.forEach(construction => {
      const customerName = construction.customerName || '未分類';
      if (!groups[customerName]) {
        groups[customerName] = [];
      }
      groups[customerName].push(construction);
    });

    return groups;
  }, [constructions]);

  // 検索用フィルター（客先名のみを対象）
  const filteredGroups = React.useMemo(() => {
    if (!searchText.trim()) return groupedConstructions;

    const result: Record<string, typeof constructions> = {};
    const lowerSearchText = searchText.toLowerCase();

    // 顧客名のみで検索
    Object.entries(groupedConstructions).forEach(([customerName, constructionList]) => {
      // 顧客名が検索テキストに一致する場合のみ、そのグループを含める
      const customerNameMatches = customerName.toLowerCase().includes(lowerSearchText);

      if (customerNameMatches) {
        // 顧客名が一致する場合はその顧客の全工事を含める
        result[customerName] = [...constructionList];
      }
    });

    return result;
  }, [groupedConstructions, searchText]);

  // ダイアログを開く
  const handleOpenDialog = () => {
    setConstructionDialogOpen(true);
    setSearchText('');

    // デフォルトで選択中の顧客を展開
    if (scheduleType === 'construction' && constructionData?.constructionId) {
      const selectedConstruction = constructions.find(
        c => c.id === constructionData.constructionId
      );
      if (selectedConstruction?.customerName) {
        setExpandedCustomers([selectedConstruction.customerName]);
      }
    }
  };

  // ダイアログを閉じる
  const handleCloseDialog = () => {
    setConstructionDialogOpen(false);
  };

  // 工事を選択した時の処理
  const handleConstructionSelectInDialog = (constructionId: number) => {
    // as any で型チェックをバイパス
    const mockEvent = {
      target: {
        name: 'constructionId',
        value: String(constructionId)
      }
    };

    if (onConstructionSelect) {
      onConstructionSelect(mockEvent as any);
      handleCloseDialog();
    }
  };

  // 検索テキスト変更時の処理
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);

    // 検索テキストがある場合は全ての顧客グループを展開
    if (e.target.value.trim()) {
      const allCustomers = Object.keys(groupedConstructions);
      setExpandedCustomers(allCustomers);
    }
  };

  // アコーディオンの展開状態の切り替え
  const handleAccordionToggle = (customerName: string) => {
    setExpandedCustomers(prev =>
      prev.includes(customerName)
        ? prev.filter(name => name !== customerName)
        : [...prev, customerName]
    );
  };

  // 顧客リストのソート（50音順）
  const sortedCustomerNames = React.useMemo(() => {
    return Object.keys(filteredGroups).sort((a, b) => a.localeCompare(b));
  }, [filteredGroups]);

  interface NumberSelectorProps {
    value: number;
    onValueChange: (value: number) => void;
    min?: number;
    max?: number;
  }

  // キャストでフォームデータの型を明示
  const constructionData = scheduleType === 'construction' ? formData as Schedule : null;
  const otherData = scheduleType === 'other' ? formData as OtherSchedule : null;

  // 選択されている工事の情報
  const selectedConstruction = constructionData?.constructionId
    ? constructions.find(c => c.id === constructionData.constructionId)
    : null;

  // 時間タイプの値を決定
  const getTimeTypeValue = () => {
    const data = scheduleType === 'construction' ? constructionData : otherData;
    if (!data) return 'custom';

    if (data.isAllDay) return 'allDay';
    if (data.startTime === '09:00' && data.endTime === '12:00') return 'morning';
    if (data.startTime === '13:00' && data.endTime === '17:00') return 'afternoon';
    return 'custom';
  };

  const handleNumberChange = (value: number) => {
    const mockEvent = {
      target: {
        name: 'requiredWorkers',
        value: value
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onChange(mockEvent);
  };

  const NumberSelector: React.FC<NumberSelectorProps> = ({ value, onValueChange, min = 0, max = 20 }) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
        <Button
          variant="contained"
          onClick={() => value > min && onValueChange(value - 1)}
          disabled={value <= min}
          sx={{ borderRadius: '50%', minWidth: '56px', height: '56px', fontSize: '1.5rem' }}
        >
          <RemoveIcon fontSize="large" />
        </Button>

        <Typography variant="h4" sx={{ mx: 3, width: '80px', textAlign: 'center' }}>
          {value}
        </Typography>

        <Button
          variant="contained"
          onClick={() => value < max && onValueChange(value + 1)}
          disabled={value >= max}
          sx={{ borderRadius: '50%', minWidth: '56px', height: '56px', fontSize: '1.5rem' }}
        >
          <AddIcon fontSize="large" />
        </Button>
      </Box>
    );
  };

  // 工事予定の場合のフォーム
  const renderConstructionForm = () => (
    <>
      {/* 班選択 */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">班</FormLabel>
        <RadioGroup
          row
          name="teamType"
          value={constructionData?.teamType || 'equipment'}
          onChange={onRadioChange}
        >
          <FormControlLabel value="equipment" control={<Radio />} label="設備班" />
          <FormControlLabel value="scaffolding" control={<Radio />} label="仮設班" />
        </RadioGroup>
      </FormControl>

      {/* 日付選択 */}
      <TextField
        name="scheduleDate"
        type="date"
        fullWidth
        label="日付"
        value={constructionData?.scheduleDate || ''}
        onChange={onChange}
        sx={{ mb: 2 }}
        InputLabelProps={{
          shrink: true,
          sx: {
            color: 'primary.main',
            fontWeight: 'medium'
          }
        }}
      />

      {/* 工事選択 - すべての画面サイズでモーダルボタン */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={handleOpenDialog}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            py: 1.5,
            height: selectedConstruction ? 'auto' : '56px',
            minHeight: '56px'
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="caption" color="text.secondary">
              工事を選択
            </Typography>
            {selectedConstruction ? (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body1" noWrap>
                  {selectedConstruction.siteName} ({selectedConstruction.customerName})
                </Typography>
                {selectedConstruction.constructionDetails && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                    {selectedConstruction.constructionDetails}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                選択してください
              </Typography>
            )}
          </Box>
        </Button>
      </Box>

      {/* 集合時間選択フィールド */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel htmlFor="meeting-time" shrink>集合時間（任意）</InputLabel>
        <TextField
          id="meeting-time"
          name="meetingTime"
          type="time"
          value={constructionData?.meetingTime || '07:00'}
          onChange={onChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 300, // 5分単位
          }}
          // モバイルではデバイスのネイティブピッカーが起動
          // デスクトップではブラウザの時間選択コントロールが表示
          sx={{
            '& input::-webkit-calendar-picker-indicator': {
              cursor: 'pointer',
            },
          }}
        />
        {/* <FormHelperText>集合時間を設定すると予定表や日報に表示されます</FormHelperText> */}
      </FormControl>


      {/* 時間選択 */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">時間</FormLabel>
        <RadioGroup
          row
          name="timeType"
          value={getTimeTypeValue()}
          onChange={onTimeTypeChange}
        >
          <FormControlLabel value="morning" control={<Radio />} label="午前" />
          <FormControlLabel value="afternoon" control={<Radio />} label="午後" />
          <FormControlLabel value="allDay" control={<Radio />} label="終日" />
        </RadioGroup>
      </FormControl>

      {/* 必要人数入力欄 */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>必要人数</FormLabel>
        <NumberSelector
          value={constructionData?.requiredWorkers || 0}
          onValueChange={(value) => handleNumberChange(value)}
          min={0}
          max={20}
        />
        <Typography variant="caption" sx={{ mt: 1 }}>
          メンバー未定でも必要な人数を入力
        </Typography>
      </FormControl>
    </>
  );

  // その他予定の場合のフォーム
  const renderOtherForm = () => (
    <>
      {/* 班選択 */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">班</FormLabel>
        <RadioGroup
          row
          name="teamType"
          value={otherData?.teamType || 'equipment'}
          onChange={onRadioChange}
        >
          <FormControlLabel value="equipment" control={<Radio />} label="設備班" />
          <FormControlLabel value="scaffolding" control={<Radio />} label="仮設班" />
        </RadioGroup>
      </FormControl>

      {/* 日付選択 */}
      <TextField
        name="scheduleDate"
        type="date"
        fullWidth
        label="日付"
        value={otherData?.scheduleDate || ''}
        onChange={onChange}
        sx={{ mb: 2 }}
        InputLabelProps={{
          shrink: true,
          sx: {
            color: 'primary.main',
            fontWeight: 'medium'
          }
        }}
      />

      {/* 予定内容 */}
      <TextField
        name="scheduleContent"
        fullWidth
        required
        label="予定内容"
        value={otherData?.scheduleContent || ''}
        onChange={onChange}
        sx={{ mb: 2 }}
        placeholder="打ち合わせ、現調など"
      />

      {/* 追加：予定内容クイック選択 */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {['打ち合わせ', '現場調査', 'その他', '給与会'].map(content => (
          <Chip
            key={content}
            label={content}
            clickable
            onClick={() => {
              // テキストフィールドを直接更新するためにイベントをエミュレート
              const mockEvent = {
                target: {
                  name: 'scheduleContent',
                  value: content
                }
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(mockEvent);
            }}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        ))}
      </Box>

      {/* 顧客選択（任意） */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="customer-label">顧客（任意）</InputLabel>
        <Select
          labelId="customer-label"
          name="customerId"
          value={String(otherData?.customerId || '0')}
          onChange={onCustomerSelect}
        >
          <MenuItem value="0">選択してください</MenuItem>
          {customers.map(customer => (
            <MenuItem key={customer.ID} value={String(customer.ID)}>
              {customer.Customer_Name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 集合時間選択フィールド */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel htmlFor="meeting-time" shrink>集合時間（任意）</InputLabel>
        <TextField
          id="meeting-time"
          name="meetingTime"
          type="time"
          value={constructionData?.meetingTime || '07:00'}
          onChange={onChange}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 300, // 5分単位
          }}
          // モバイルではデバイスのネイティブピッカーが起動
          // デスクトップではブラウザの時間選択コントロールが表示
          sx={{
            '& input::-webkit-calendar-picker-indicator': {
              cursor: 'pointer',
            },
          }}
        />
        {/* <FormHelperText>集合時間を設定すると予定表や日報に表示されます</FormHelperText> */}
      </FormControl>

      {/* 時間選択 */}
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">時間</FormLabel>
        <RadioGroup
          row
          name="timeType"
          value={getTimeTypeValue()}
          onChange={onTimeTypeChange}
        >
          <FormControlLabel value="morning" control={<Radio />} label="午前" />
          <FormControlLabel value="afternoon" control={<Radio />} label="午後" />
          <FormControlLabel value="allDay" control={<Radio />} label="終日" />
        </RadioGroup>
      </FormControl>

      {/* 必要人数入力欄 */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>必要人数</FormLabel>
        <NumberSelector
          value={constructionData?.requiredWorkers || 0}
          onValueChange={(value) => handleNumberChange(value)}
          min={0}
          max={20}
        />
        <Typography variant="caption" sx={{ mt: 1 }}>
          メンバー未定でも必要な人数を入力
        </Typography>
      </FormControl>
    </>
  );

  return (
    <>
      {scheduleType === 'construction' ? renderConstructionForm() : renderOtherForm()}

      {/* メモ入力欄 */}
      <TextField
        fullWidth
        name="notes"
        label="メモ"
        multiline
        rows={4}
        value={formData.notes || ''}
        onChange={onChange}
        sx={{ mt: 2 }}
      />

      {/* 工事選択ダイアログ */}
      <Dialog
        open={constructionDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1
        }}>
          <Typography variant="h6">工事を選択</Typography>
          <IconButton onClick={handleCloseDialog} edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          {/* 検索フィールド */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="客先名で検索..."
              value={searchText}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                display: 'block',
                mt: 0.5,
                fontWeight: 'bold'
              }}
            >
              ※検索対象は客先名のみです
            </Typography>
          </Box>

          {/* 顧客別工事一覧 */}
          {sortedCustomerNames.length > 0 ? (
            sortedCustomerNames.map(customerName => (
              <Accordion
                key={customerName}
                expanded={expandedCustomers.includes(customerName)}
                onChange={() => handleAccordionToggle(customerName)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {customerName} ({filteredGroups[customerName]?.length || 0}件)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List sx={{ pt: 0, pb: 0 }}>
                    {filteredGroups[customerName]?.map((construction, index) => (
                      <React.Fragment key={construction.id}>
                        <ListItem
                          button
                          onClick={() => handleConstructionSelectInDialog(construction.id)}
                          selected={constructionData?.constructionId === construction.id}
                          sx={{
                            py: 1.5,
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.primary.light,
                              '&:hover': {
                                backgroundColor: theme.palette.primary.light,
                              }
                            }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight="medium">
                                {construction.siteName}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {construction.constructionDetails && (
                                  <Typography variant="body2" color="text.secondary">
                                    {construction.constructionDetails}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < filteredGroups[customerName].length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchText ? '検索結果がありません' : '工事情報がありません'}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit">キャンセル</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};